<script setup lang="ts">
import { formatCepMasked, normalizeCepDigits } from '@solfacil/frontend-utils/cep';
import { computed } from 'vue';

import InfoGridItem from '~/components/shared/InfoGridItem.vue';
import { FinancingFlowApi } from '~/services/financing-flow-api/financing-flow.types';

import type { CustomerRegisterApi } from '../../services/customer-register-api/customer-register-api.types';

import useCustomerRegistrationDetails from '../../queries/useCustomerRegistrationDetails';

interface Props {
  isLoading?: boolean;
  personType: FinancingFlowApi.FinancingV2['person_type'];
  projectId: string;
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
});

const registrationQuery = useCustomerRegistrationDetails(computed(() => props.projectId));
const registrationData = computed(() => registrationQuery.data.value);
const internalIsLoading = computed(
  () => props.isLoading || registrationQuery.isLoading.value || registrationQuery.isFetching.value,
);

const formatBoolean = (value?: boolean) => {
  if (value === undefined || value === null) return null;
  return value ? 'Sim' : 'Não';
};

const addressData = computed(() => {
  const data = registrationData.value;
  if (!data) return [];

  let address: CustomerRegisterApi.Address | undefined;

  if (props.personType === 'PF') {
    address = data.customer?.address;
  } else if (props.personType === 'PJ') {
    address = data.company?.majority_partner?.address;
  }

  if (!address) return [];

  return [
    { label: 'CEP', value: address.zipcode ? formatCepMasked(normalizeCepDigits(address.zipcode)) : null },
    { label: 'Rua', value: address.street || null },
    { label: 'Número', value: address.street_number || null },
    { label: 'Complemento', value: address.complement || null },
    { label: 'Bairro', value: address.neighborhood || null },
    { label: 'Cidade', value: address.city?.name || null },
    { label: 'Estado', value: address.state?.name || null },
    { label: 'Zona Rural', value: formatBoolean(address.is_rural) },
  ].filter((item) => item.value !== null);
});

const hasData = computed(() => addressData.value.length > 0);

const skeletonAddressData = computed(() => {
  if (!internalIsLoading.value) return [];
  return [
    { label: 'CEP', value: null },
    { label: 'Rua', value: null },
    { label: 'Número', value: null },
    { label: 'Complemento', value: null },
    { label: 'Bairro', value: null },
    { label: 'Cidade', value: null },
    { label: 'Estado', value: null },
    { label: 'Zona Rural', value: null },
  ];
});
</script>

<template>
  <div class="address-data-content">
    <div v-if="!internalIsLoading && !hasData" class="empty-state">
      <p class="empty-message">Dados ainda não preenchidos</p>
    </div>

    <div v-else class="datagrid">
      <InfoGridItem
        v-for="item in (internalIsLoading ? skeletonAddressData : addressData)"
        :key="item.label"
        :label="item.label"
        :value="item.value"
        :is-loading="internalIsLoading"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.address-data-content {
  .datagrid {
    display: grid;
    gap: 0.75rem;
  }

  .empty-state {
    padding: 2rem;
    text-align: center;
  }

  .empty-message {
    color: #94a3b8;
    font-size: 0.9375rem;
    font-style: italic;
    margin: 0;
  }
}
</style>
